import Bugsnag from '@bugsnag/js'
import type { User } from '@/stores/auth'
import {
  type ChangePasswordRequest,
  type CheckEmailResponse,
  type CompleteOnboardingRequest,
  type CompleteOnboardingResponse,
  type ForgotPasswordRequest,
  type GetOrganizationsResponse,
  type LoginRequest,
  type LoginResponse,
  type Organization,
  type RefreshTokenResponse,
  type RegisterRequest,
  type RegisterResponse,
  type ResendVerificationRequest,
  type ResetPasswordRequest,
  type Role,
  type SwitchOrganizationRequest,
  type SwitchOrganizationResponse,
  type UpdateOrganizationDomainRequest,
  type UpdateOrganizationDomainResponse,
  type UpdateProfileRequest,
  useAuthStore,
  type ValidateResetTokenRequest,
  type ValidateResetTokenResponse,
  type VerifyEmailRequest,
} from '@/stores/auth'
import {
  type ApiError,
  type ApiResponse,
  apiClient,
  handleApiError,
  handleApiResponse,
} from './api'

function notifyAuthError(
  operation: string,
  error: unknown,
  metadata?: Record<string, unknown>
) {
  if (!Bugsnag.isStarted()) return

  const apiError = error as ApiError | undefined
  const errorObj =
    error instanceof Error
      ? error
      : new Error(apiError?.message || `Auth ${operation} failed`)

  Bugsnag.notify(errorObj, (event) => {
    event.context = `auth/${operation}`
    event.groupingHash = `auth-${operation}-${apiError?.status || 'unknown'}`
    event.addMetadata('auth', {
      operation,
      status: apiError?.status,
      code: apiError?.code,
      serverMessage: apiError?.message,
      validationErrors: apiError?.errors,
      ...metadata,
    })
  })
}

// Token format validation - Rails tokens are typically alphanumeric with some special chars
// This validates format before sending to the API as a defense-in-depth measure
const TOKEN_PATTERN = /^[a-zA-Z0-9_-]{20,256}$/

function isValidTokenFormat(token: string): boolean {
  return TOKEN_PATTERN.test(token)
}

class AuthService {
  // Register new user
  async register(
    data: RegisterRequest & { invitationToken?: string }
  ): Promise<RegisterResponse> {
    try {
      const requestData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        organizationName: data.organizationName,
        ...(data.invitationToken && { invitation_token: data.invitationToken }),
      }

      const response = await apiClient.post<ApiResponse<RegisterResponse>>(
        '/auth/register',
        requestData
      )

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('register', apiError, {
        email: data.email,
        hasInvitation: !!data.invitationToken,
        organizationName: data.organizationName,
      })
      throw apiError
    }
  }

  // Login user
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const requestData = {
        auth: {
          email: credentials.email,
          password: credentials.password,
        },
      }

      const response = await apiClient.post<ApiResponse<LoginResponse>>(
        '/auth/login',
        requestData
      )

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('login', apiError, { email: credentials.email })
      throw apiError
    }
  }

  // Google Sign-In — exchange a Google access token for app JWTs
  async googleSignIn(accessToken: string): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<ApiResponse<LoginResponse>>(
        '/auth/google-signin',
        { accessToken }
      )
      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('googleSignIn', apiError)
      throw apiError
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await apiClient.delete('/auth/logout')
    } catch (error) {
      // Even if logout fails on server, we still want to clear local state
      console.warn('Logout request failed:', error)
    }
  }

  // Refresh access token
  async refreshToken(
    refreshToken: string,
    organizationId?: string
  ): Promise<RefreshTokenResponse> {
    try {
      const response = await apiClient.post<ApiResponse<RefreshTokenResponse>>(
        '/auth/refresh',
        { refreshToken, organizationId }
      )

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('refreshToken', apiError)
      throw apiError
    }
  }

  // Get current user profile
  async getCurrentUser(): Promise<User> {
    try {
      // /auth/me returns { user, organization, role, available_organizations }
      const response =
        await apiClient.get<ApiResponse<{ user: User }>>('/auth/me')
      const data = handleApiResponse(response)

      return data.user
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('getCurrentUser', apiError)
      throw apiError
    }
  }

  // Get full auth context (user, organization, role, permissions) from /auth/me
  async getAuthContext(): Promise<{
    user: User
    organization: Organization
    role: Role
    permissions: string[]
    availableOrganizations: Array<{ organization: Organization; role: Role }>
  }> {
    try {
      const response =
        await apiClient.get<
          ApiResponse<{
            user: User
            organization: Organization
            role: Role
            permissions?: string[]
            availableOrganizations: Array<{
              organization: Organization
              role: Role
            }>
          }>
        >('/auth/me')

      const data = handleApiResponse(response)
      return { ...data, permissions: data.permissions ?? [] }
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('getAuthContext', apiError)
      throw apiError
    }
  }

  // Update user profile
  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    try {
      const response = await apiClient.patch<ApiResponse<User>>(
        '/auth/profile',
        data
      )

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('updateProfile', apiError)
      throw apiError
    }
  }

  // Change password
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    try {
      const response = await apiClient.patch<ApiResponse<null>>(
        '/auth/change-password',
        data
      )

      handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('changePassword', apiError)
      throw apiError
    }
  }

  // Request password reset
  async requestPasswordReset(request: ForgotPasswordRequest): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse<null>>(
        '/auth/forgot-password',
        request
      )

      handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('requestPasswordReset', apiError, {
        email: request.email,
      })
      throw apiError
    }
  }

  // Reset password with token
  async resetPassword(request: ResetPasswordRequest): Promise<void> {
    if (!request.token || !isValidTokenFormat(request.token)) {
      throw new Error('Invalid reset token format')
    }

    try {
      const response = await apiClient.post<ApiResponse<null>>(
        '/auth/reset-password',
        request
      )

      handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('resetPassword', apiError)
      throw apiError
    }
  }

  // Validate password reset token (check if valid before showing form)
  async validateResetToken(
    request: ValidateResetTokenRequest
  ): Promise<ValidateResetTokenResponse> {
    if (!request.token || !isValidTokenFormat(request.token)) {
      return { valid: false, expired: false }
    }

    try {
      const response = await apiClient.get<
        ApiResponse<ValidateResetTokenResponse>
      >(`/auth/validate-reset-token?token=${encodeURIComponent(request.token)}`)

      return handleApiResponse(response)
    } catch {
      return { valid: false, expired: false }
    }
  }

  // Verify email with token
  async verifyEmail(request: VerifyEmailRequest): Promise<void> {
    if (!request.token || !isValidTokenFormat(request.token)) {
      throw new Error('Invalid verification token format')
    }

    try {
      const response = await apiClient.post<ApiResponse<null>>(
        '/auth/verify-email',
        request
      )

      handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('verifyEmail', apiError)
      throw apiError
    }
  }

  // Resend email verification
  async resendEmailVerification(
    request: ResendVerificationRequest
  ): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse<null>>(
        '/auth/resend-verification',
        request
      )

      handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('resendVerification', apiError, {
        email: request.email,
      })
      throw apiError
    }
  }

  // Check if email exists (for registration flow)
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const response = await apiClient.get<ApiResponse<CheckEmailResponse>>(
        `/auth/check-email?email=${encodeURIComponent(email)}`
      )

      const result = handleApiResponse(response)

      return result.exists
    } catch {
      // If endpoint doesn't exist or fails, assume email doesn't exist
      return false
    }
  }

  // Get available organizations for current user
  async getOrganizations(): Promise<GetOrganizationsResponse> {
    try {
      const response = await apiClient.get<
        ApiResponse<GetOrganizationsResponse>
      >('/auth/organizations')

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('getOrganizations', apiError)
      throw apiError
    }
  }

  // Switch organization
  async switchOrganization(
    request: SwitchOrganizationRequest
  ): Promise<SwitchOrganizationResponse> {
    try {
      const response = await apiClient.post<
        ApiResponse<SwitchOrganizationResponse>
      >('/auth/switch-organization', request)

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('switchOrganization', apiError, {
        organizationId: request.organizationId,
      })
      throw apiError
    }
  }

  // Connect a BlueConic account to an organization
  async connectBlueconic(request: {
    blueconicAccountId: string
    organizationId: string
  }): Promise<{ organization: Organization }> {
    try {
      const response = await apiClient.post<
        ApiResponse<{ organization: Organization }>
      >('/blueconic/connect', request)

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('connectBlueconic', apiError, {
        organizationId: request.organizationId,
      })
      throw apiError
    }
  }

  async completeOnboarding(
    request: CompleteOnboardingRequest
  ): Promise<CompleteOnboardingResponse> {
    try {
      const response = await apiClient.post<
        ApiResponse<CompleteOnboardingResponse>
      >('/auth/complete-onboarding', request)

      const result = handleApiResponse(response)

      if (!result.user) {
        throw new Error('User not returned from complete-onboarding')
      }

      const state = useAuthStore.getState()
      state.setUser(result.user)

      return result
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('completeOnboarding', apiError)
      throw apiError
    }
  }

  // Update organization domain
  async updateOrganizationDomain(
    request: UpdateOrganizationDomainRequest
  ): Promise<UpdateOrganizationDomainResponse> {
    try {
      const response = await apiClient.patch<
        ApiResponse<UpdateOrganizationDomainResponse>
      >('/auth/organization/domain', request)

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      notifyAuthError('updateOrganizationDomain', apiError)
      throw apiError
    }
  }
}

// Create and export singleton instance
export const authService = new AuthService()

// Export types for convenience
export type { ApiError }
export * from '@/stores/auth'
